"use client";

import { memo, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { PopoverClose } from "@radix-ui/react-popover";
import { QuantityStepper } from "@/components/shared/QuantityStepper";
import { travelerFieldConfig } from "../search.modules.type";
import { Typography } from "../../Typography";
import { Button } from "../../ui/button";
import { Users } from "lucide-react";
import { capitalizeFirstLetter, cn } from "@/lib/utils";

const clampCount = (value: number, minimum = 0) => Math.max(minimum, value);
const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

export type TravelerCounts = {
  adults: number;
  children: number;
  rooms: number;
};

export type TravelerPickerProps = {
  value?: TravelerCounts;
  onChange?: (next: TravelerCounts) => void;
  inlineExpanded?: boolean;
  onInlineExpandedChange?: (open: boolean) => void;
  className?: string;
  rounded?: "normal" | "full";
  leadingIcon?: React.ReactNode;
  ariaLabel?: string;
  error?: boolean;
};

const TravelerPicker = ({
  value,
  onChange,
  inlineExpanded,
  onInlineExpandedChange,
  className,
  rounded = "full",
  leadingIcon,
  ariaLabel = "Select travelers",
  error = false,
}: TravelerPickerProps) => {
  const contentId = "traveler-picker-popover";
  const isInline = typeof onInlineExpandedChange === "function";
  const [internalCounts, setInternalCounts] = useState<TravelerCounts>({
    adults: 2,
    children: 0,
    rooms: 1,
  });

  const travelerCounts = value ?? internalCounts;
  const isInlineOpen = Boolean(inlineExpanded);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const isOpen = isInline ? isInlineOpen : popoverOpen;

  const setTravelerCounts = (next: TravelerCounts) => {
    onChange?.(next);
    if (!value) {
      setInternalCounts(next);
    }
  };

  const summary = useMemo(() => {
    const tuples = [
      [travelerCounts.adults, "adult", "adults"],
      [travelerCounts.children, "child", "children"],
      [travelerCounts.rooms, "room", "rooms"],
    ] as const;

    if (rounded === "normal") {
      return tuples
        .filter(([count, singular]) => singular !== "child" || count > 0)
        .map(([count, singular, plural]) => pluralize(count, singular, plural))
        .join(" · ");
    }

    return (
      <div className="flex items-center gap-2">
        {tuples.map(([count, singular, plural], idx) => (
          <span key={singular}>
            {pluralize(
              count,
              capitalizeFirstLetter(singular),
              capitalizeFirstLetter(plural),
            )}
            {idx < tuples.length - 1 ? "," : ""}
          </span>
        ))}
      </div>
    );
  }, [travelerCounts, rounded]);

  const contentBody = (
    <>
      <div className="space-y-4">
        <Typography variant="subtitle1">Select Guests & Rooms</Typography>

        <div className="space-y-3">
          {travelerFieldConfig.map((item) => (
            <div key={item.key} className="flex items-center justify-between">
              <Typography variant="body1">{item.label}</Typography>

              <QuantityStepper
                value={travelerCounts[item.key]}
                min={item.minimum}
                label={item.label.toLowerCase()}
                onDecrement={() => {
                  const nextValue = clampCount(
                    travelerCounts[item.key] - 1,
                    item.minimum,
                  );
                  setTravelerCounts({
                    ...travelerCounts,
                    [item.key]: nextValue,
                  });
                }}
                onIncrement={() =>
                  setTravelerCounts({
                    ...travelerCounts,
                    [item.key]: travelerCounts[item.key] + 1,
                  })
                }
              />
            </div>
          ))}
        </div>
      </div>
      {isInline ? (
        <div className="mt-2 h-[50px] w-full px-4 text-sm font-semibold">
          <Button
            variant={"ghost"}
            onClick={() => onInlineExpandedChange?.(false)}
            className="ml-auto block w-fit text-secondary"
          >
            Done
          </Button>
        </div>
      ) : (
        <PopoverClose asChild>
          <Button
            type="button"
            variant="ghost"
            className="ml-auto block w-fit text-secondary mt-2"
          >
            Done
          </Button>
        </PopoverClose>
      )}
    </>
  );

  const trigger = (
    <button
      type="button"
      onClick={() => {
        if (isInline) {
          onInlineExpandedChange?.(!isInlineOpen);
        }
      }}
      className={cn(
        "flex h-[46px] w-full min-w-0 items-center gap-2 border bg-white text-left hover:border-primary md:h-12 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary",
        rounded === "normal" ? "rounded-xl px-3.5" : "rounded-full px-4",
        isOpen && "border-primary ring-1 ring-primary",
        error && "border-error-foreground focus-visible:ring-error-foreground",
        className,
      )}
      aria-label={ariaLabel}
      aria-controls={contentId}
      aria-expanded={isOpen}
    >
      {leadingIcon ? (
        <span className="shrink-0 flex items-center justify-center text-body">
          {leadingIcon}
        </span>
      ) : (
        <Users size={20} className="text-body shrink-0" />
      )}
      <Typography
        variant="body1"
        className={cn(
          "truncate text-base text-body",
          rounded === "normal" ? "font-normal" : "font-semibold",
        )}
      >
        {summary}
      </Typography>
    </button>
  );

  if (isInline) {
    return (
      <div className="space-y-2">
        {trigger}
        <AnimatePresence initial={false}>
          {isInlineOpen ? (
            <motion.div
              id={contentId}
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
              className="rounded-2xl border bg-white p-4"
            >
              {contentBody}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverAnchor asChild>
        <div className="w-full min-w-0">
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        id={contentId}
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        style={{ width: "var(--radix-popper-anchor-width)" }}
        className="z-20 min-w-[280px] max-w-none rounded-3xl border bg-white p-4 shadow-2xl"
      >
        {contentBody}
      </PopoverContent>
    </Popover>
  );
};

export default memo(TravelerPicker);
